import { InvoicingAddressFields, InvoicingAddressFormFields, useInvoicingAddressForm } from 'components/forms/InvoicingAddress'
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query'

import { FC } from 'react'
import { MUIButton } from '../MUIButton'
import Modal from '../Modals/Modal/Modal'
import { QueryStatus } from '../QueryStatus'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

/**
 * Defines the available modes for the invoicing address popup.
 * - `'create'`: Used when creating a new invoicing address.
 * - `'edit'`: Used when editing an existing invoicing address.
 */
export type InvoicingAddressPopupMode = 'create' | 'edit'

/**
 * Props for the {@link InvoicingAddressPopupController} component.
 */
export interface InvoicingAddressPopupControllerProps {
  /** Whether the popup modal is currently open. */
  isOpen: boolean

  /** Reference to query or mutation. */
  query: UseMutationResult<any, any, any, any> | UseQueryResult<any, any>

  /** Determines the mode of the popup — `'create'` or `'edit'`. Defaults to `'create'`. */
  mode?: InvoicingAddressPopupMode

  /**
   * Callback executed when the form is submitted.
   * @param values - The invoicing address form values.
   */
  onSubmit: (values: InvoicingAddressFields) => void

  /** Callback executed when the modal is closed. */
  onClose: () => void
}

/**
 * Renders a modal popup for creating or editing invoicing addresses.
 *
 * This component manages:
 * - Modal open/close behavior
 * - Form reset after closing
 * - Submit and cancel actions
 * - Translations for dynamic titles and button labels
 *
 * @component
 * @example
 * ```tsx
 * <InvoicingAddressPopupController
 *   isOpen={isOpen}
 *   mode="edit"
 *   onSubmit={handleSubmit}
 *   onClose={handleClose}
 * />
 * ```
 */
export const InvoicingAddressPopupController: FC<InvoicingAddressPopupControllerProps> = ({
  query,
  onSubmit,
  onClose,
  isOpen,
  mode = 'create',
}) => {

  const { t } = useTranslation(['invoicing_address_popup', 'common'])

  const {
    getFormValues,
    isValid,
    reset,
  } = useInvoicingAddressForm()

  return (
    <Modal
      title={t(`${mode}.title`)}
      isOpen={isOpen}
      onClose={() => onClose()}
      afterClosed={() => reset()}
      contentStyle={{
        width: '90vw',
        maxWidth: '70rem'
      }}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={1}>

          <MUIButton
            size="md"
            type="secondaryBorder"
            disabled={query.isPending}
            onClick={() => onClose()}
          >
            {t('common:Cancel')}
          </MUIButton>

          <MUIButton
            size="md"
            disabled={!isValid || query.isPending}
            isLoading={query.isPending}
            onClick={() => onSubmit(getFormValues())}
          >
            {t(`${mode}.submit`)}
          </MUIButton>
        </Stack>
      }
    >
      <InvoicingAddressFormFields />

      <QueryStatus
        query={query}
        showStates={['error']}
      />

    </Modal>
  )
}
